import { useEffect, useRef, useState } from "react";
import { useLocation, useParams, useSearchParams, Link as RouterLink } from "react-router-dom";
import { UtensilsCrossed, ShoppingBag, Bike, QrCode } from "lucide-react";
import { ShareButton } from "../components/ShareButton";
import { restaurantJsonLd } from "../data/nap";
import { DeliveryBadges } from "../components/DeliveryBadges";
import { DeliveryBand } from "../components/DeliveryBand";
import api from "../lib/api";
import { imgProps } from "../lib/img";
import { groupProducts, MenuCategoryBoxes, MenuCategoryView, MenuSearchResults, ThisWeekStrip, OurStoryTeaser, setBoxHandler } from "../components/MenuCategoryBoxes";
import { applyVariationGroups, entryMatches } from "../lib/variations";
import { usePageSeo } from "../lib/seo";
import OffersStrip from "../components/OffersStrip";
import { useCart } from "../context/CartContext";
import ProductCard from "../components/ProductCard";
import ItemDetailSheet from "../components/ItemDetailSheet";
import ComingSoonStore from "../components/ComingSoonStore";
import { useProductDeals } from "../lib/usePromos";
import { Editable } from "../components/edit/Editable";
import { EditEntityButton } from "../components/edit/EntityEditor";
import { useContent } from "../context/ContentContext";
import seoContent from "../data/seo_content.json";

const STORE_SEO_LINE = seoContent.storeSeoLine || {};

const CHANNEL_META = {
  dine_in: { label: "Dine-in", icon: UtensilsCrossed },
  pickup: { label: "Pickup", icon: ShoppingBag },
  delivery: { label: "Delivery", icon: Bike },
};

const STORE_BANNER_ITEM = {
  restaurant: "tb_chicago_dhaba_mixed_grill",
  halwai: "hw_gulab_jamun",
  express: "ex_butter_chicken_pocket",
  bakery: "bk_creme_brulee_cup",
};

const EXPRESS_BIZ_ID = "biz_express";
const EXPRESS_NAME = "Urban Express";

const HERO_VIDEOS = {
  // Videos retired per owner preference for photo heroes; drop real footage here anytime.
};

const HERO_KICKERS = {
  restaurant: "Modern Punjabi Dhaba · Chicago",
  halwai: "Fresh Mithai, Made Daily",
  bakery: "100% Eggless · Baked Fresh",
  express: "Indian Street Food · Grab & Go",
  bodega: "Indian Grocery · The Pantry Behind the Kitchen",
};

const HALWAI_FAQ = [
  ["What mithai boxes do you have?", "Ladoo, Kalakand, Besan Barfi, Kaju Katli, Barfi, Petha, Mixed Mithai, Jalebi, Gulab Jamun, Rasgulla, Ras Malai and Milk Cake boxes."],
  ["What sizes are the boxes?", "Every mithai box is a 1/2 lb box."],
  ["How do I order?", "Order online on this page for pickup at 2660 N Halsted St. Hours 2 PM to 11 PM, closed Tuesday."],
  ["What is in an Urban Halwai mithai box?", "One kind of Indian sweet per box, 1/2 lb, except the Mixed Mithai Box, which has an assortment."],
  ["How much does a mithai box cost?", "$10 to $16 for a 1/2 lb box."],
  ["Is Urban Halwai part of Urban Turban?", "Yes. Urban Halwai is the Indian sweets counter of Urban Turban by VeerGi, at the same address and hours."],
  ["Do you have Diwali mithai?", "Yes, every box is available for Diwali; see tryurbanturban.com/diwali-mithai-chicago."],
];
const HALWAI_FAQ_LD = {
  "@context": "https://schema.org", "@type": "FAQPage",
  mainEntity: HALWAI_FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
};
const HALWAI_INTRO = "Indian Diwali mithai, made Punjabi style. Every box is a half pound (1/2 lb) box, for pickup at 2660 N Halsted St.";
const HALWAI_PICKUP_LINE = "Pickup at 2660 N Halsted St in Lincoln Park, near DePaul, Lake View and Old Town.";
const HALWAI_CANON = seoContent.canon;
const HALWAI_LOCALBUSINESS_LD = {
  "@context": "https://schema.org", "@type": ["FoodEstablishment", "Store"], "@id": `${HALWAI_CANON}/store/halwai#halwai`,
  name: "Urban Halwai by VeerGi", description: "Indian sweets and mithai boxes, made Punjabi style",
  url: `${HALWAI_CANON}/store/halwai`, telephone: "+13129550331",
  address: { "@type": "PostalAddress", streetAddress: "2660 N Halsted St", addressLocality: "Chicago", addressRegion: "IL", postalCode: "60614", addressCountry: "US" },
  geo: { "@type": "GeoCoordinates", latitude: seoContent.nap.geo.lat, longitude: seoContent.nap.geo.lng },
  openingHoursSpecification: [
    { "@type": "OpeningHoursSpecification", dayOfWeek: ["Sunday", "Monday", "Wednesday", "Thursday", "Friday", "Saturday"], opens: "14:00", closes: "23:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Tuesday", opens: "00:00", closes: "00:00" }],
  servesCuisine: ["Indian", "Punjabi"], priceRange: "$10-$16", hasMenu: `${HALWAI_CANON}/store/halwai`,
  parentOrganization: { "@id": `${HALWAI_CANON}/#restaurant` },
  ...(seoContent.googleBusinessProfile ? { sameAs: [seoContent.googleBusinessProfile] } : {}),
};

export default function BusinessPage({ qrMode = false }) {
  const { slug, table } = useParams();
  const routerLoc = useLocation();
  const { channel, setChannel, deliveryInfo, setTableNumber, tableNumber } = useCart();
  const [business, setBusiness] = useState(null);
  const [categories, setCategories] = useState([]);
  const [expressCats, setExpressCats] = useState([]);
  const [variationGroups, setVariationGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [highlightId, setHighlightId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [thisWeek, setThisWeek] = useState({ combos: [], items: [] });
  const [detailProduct, setDetailProduct] = useState(null);
  const gridRef = useRef(null);
  const dealFor = useProductDeals(business?.id);
  const { editMode } = useContent();
  const festive = business?.type === "food" && !editMode;  // every food brand (Urban Turban, Urban Express) uses the same menu template
  const menuCats = business?.slug === "restaurant" ? [...categories, ...expressCats] : categories;  // feed order: Urban Turban sections, then Urban Express
  const reloadCategories = () => business && api.get(`/businesses/${business.id}/categories`, { params: { include_hidden: editMode } }).then((r) => setCategories(r.data));
  const onProductEdited = (np) => setProducts((all) => (np.active === false ? all.filter((x) => x.id !== np.id) : all.map((x) => (x.id === np.id ? { ...x, ...np } : x))));

  const scrollToMenu = () => (gridRef.current || document.getElementById("menu-category-boxes"))?.scrollIntoView({ behavior: "smooth", block: "start" });

  const heroClick = () => {
    const target = STORE_BANNER_ITEM[business?.slug];
    if (target && products.some((p) => p.id === target)) selectFeatured({ id: target });
    else scrollToMenu();
  };

  const selectFeatured = (p) => {
    setActiveCat("all");
    setSearch("");
    if (festive) {
      const g = groupProducts(applyVariationGroups(products, variationGroups), menuCats).find((x) => x.items.some((i) => i.product.id === p.id));
      if (g && searchParams.get("cat") !== g.key) setSearchParams({ cat: g.key });
    }
    setHighlightId(p.id);
    setTimeout(() => document.getElementById(`prod-${p.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
    setTimeout(() => setHighlightId(null), 3000);
  };

  useEffect(() => {
    if (business?.type !== "food") return;
    api.get("/storefront/featured", { params: { business_id: business.id, channel } }).then(({ data }) => setThisWeek(data)).catch(() => setThisWeek({ combos: [], items: [] }));
  }, [business?.id, business?.type, channel]);

  // Urban Express feed categories (listed after the Urban Turban ones) + POS variation groups (empty list → flat menu)
  useEffect(() => {
    if (business?.slug !== "restaurant") return;
    api.get(`/businesses/${EXPRESS_BIZ_ID}/categories`).then((r) => setExpressCats(r.data.map((c) => ({ ...c, express: true })))).catch(() => setExpressCats([]));
    Promise.all([api.get(`/businesses/${business.id}/variation-groups`), api.get(`/businesses/${EXPRESS_BIZ_ID}/variation-groups`)])
      .then(([a, b]) => setVariationGroups([...a.data, ...b.data])).catch(() => setVariationGroups([]));
  }, [business?.id, business?.slug]);

  useEffect(() => {
    if (business?.slug !== "express") return;
    api.get(`/businesses/${business.id}/variation-groups`).then((r) => setVariationGroups(r.data)).catch(() => setVariationGroups([]));
  }, [business?.id, business?.slug]);

  useEffect(() => {
    const fid = routerLoc.state?.featuredId;
    if (fid && products.length) selectFeatured({ id: fid });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLoc.state, products.length]);

  const [menuLd, setMenuLd] = useState(null);
  const [mithai, setMithai] = useState([]);
  useEffect(() => {
    if (slug !== "halwai") { setMithai([]); return; }
    api.get("/mithai").then(({ data }) => setMithai(data.items || [])).catch(() => setMithai([]));
  }, [slug]);
  useEffect(() => {
    setMenuLd(null);
    api.get(`/seo/menu-jsonld/${slug}`).then(({ data }) => setMenuLd(data.jsonld)).catch(() => setMenuLd(null));
  }, [slug]);

  usePageSeo(business ? {
    title: business.seo?.title || `${business.name} Menu — Order Online | Urban Turban by VeerGi`,
    description: business.seo?.description || business.description,
    canonicalPath: `/store/${slug}`,
    robots: business.is_public ? undefined : "noindex, follow",
    ogImage: business.image,
    jsonLd: [...(menuLd ? [menuLd] : []), ...(slug === "restaurant" ? [restaurantJsonLd({})] : []), ...(slug === "halwai" ? [HALWAI_FAQ_LD, HALWAI_LOCALBUSINESS_LD] : [])],
  } : {});

  useEffect(() => {
    if (qrMode) {
      setChannel("dine_in");
      setTableNumber(table || "");
    }
  }, [qrMode, table, setChannel, setTableNumber]);

  useEffect(() => {
    api.get(`/businesses/${slug}`).then(({ data }) => {
      setBusiness(data);
      if (data.image && !HERO_VIDEOS[data.slug] && !document.querySelector('link[rel="preload"][data-hero]')) {
        const hp = imgProps(data.image, [800, 1200, 1600], "100vw");
        const l = document.createElement("link");
        l.rel = "preload"; l.as = "image"; l.href = hp.src; l.setAttribute("fetchpriority", "high"); l.dataset.hero = "1";
        if (hp.srcSet) { l.setAttribute("imagesrcset", hp.srcSet); l.setAttribute("imagesizes", hp.sizes); }
        document.head.appendChild(l);
      }
      if (!qrMode && !data.channels.includes(channel)) setChannel(data.channels[0]);
      api.get(`/businesses/${data.id}/categories`, { params: { include_hidden: editMode } }).then((r) => setCategories(r.data));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, editMode]);

  useEffect(() => {
    if (!business) return;
    const ch = qrMode ? "dine_in" : channel;
    const own = api.get(`/businesses/${business.id}/products`, { params: { channel: ch } })
      .then(({ data }) => (business.slug === "halwai" ? data.map((p) => ({ ...p, description: `1/2 lb box${p.description ? ` · ${p.description}` : ""}` })) : data));
    // Urban Turban page also lists Urban Express items under their own "Urban Express" category (the Express tab stays as is).
    const express = business.slug === "restaurant"
      ? api.get(`/businesses/${EXPRESS_BIZ_ID}/products`, { params: { channel: ch } }).then(({ data }) => data.map((p) => ({ ...p, business_name: EXPRESS_NAME }))).catch(() => [])
      : Promise.resolve([]);
    Promise.all([own, express]).then(([a, b]) => setProducts([...b, ...a]));
  }, [business, channel, qrMode]);

  // Menu JSON-LD: exactly one block, injected via usePageSeo (menuLd from /seo/menu-jsonld)

  if (!business) return <div className="min-h-screen bg-[#FDFBF7] p-12 text-center text-neutral-400"><div className="h-72 sm:h-[24rem] -mx-12 -mt-12 bg-neutral-900" />Loading…</div>;
  if (business.ordering === "coming_soon") return <ComingSoonStore business={business} kicker={HERO_KICKERS[business.slug] || business.tagline} />;

  const availableChannels = qrMode ? ["dine_in"] : business.channels;
  const specialCat = categories.find((c) => /today.?s special/i.test(c.name || ""));
  const hasVisible = (c) => products.some((p) => p.category_id === c.id || (specialCat && c.id === specialCat.id && p.todays_special));
  const allCats = menuCats;
  const shownCats = editMode ? allCats : allCats.filter(hasVisible);
  const visible = activeCat === "all" ? [...products].sort((x, y) => (x.business_id === EXPRESS_BIZ_ID) - (y.business_id === EXPRESS_BIZ_ID))
    : products.filter((p) => p.category_id === activeCat || (specialCat && activeCat === specialCat.id && p.todays_special));
  const entries = festive ? applyVariationGroups(visible, variationGroups) : [];
  const groups = festive ? groupProducts(entries, menuCats) : [];
  const activeGroup = festive ? groups.find((g) => g.key === searchParams.get("cat")) : null;
  const searching = festive && !activeGroup && search.trim().length > 0;
  const searchHits = searching ? entries.filter((e) => entryMatches(e, search)) : [];
  const openGroup = (key) => { setSearch(""); setSearchParams({ cat: key }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const backToMenu = () => { setSearchParams({}); setTimeout(() => document.getElementById("menu-category-boxes")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };
  setBoxHandler(festive ? openGroup : null);
  const weekFeatured = [...thisWeek.combos, ...thisWeek.items].filter((p) => p.in_stock !== false);
  const weekCards = weekFeatured.length || business.slug !== "halwai" ? weekFeatured : products.filter((p) => p.in_stock !== false).slice(0, 3);
  const groceryDeals = business.slug === "express" ? products.filter((p) => p.on_sale && p.id.startsWith("exg_")) : [];
  const renderDish = (p) => (
    <div key={p.id} id={`prod-${p.id}`} className={highlightId === p.id ? "ring-2 ring-[#E04F33] ring-offset-2 rounded-2xl transition-shadow" : ""}>
      <ProductCard product={{ ...p, business_name: p.business_name || business.name }} deal={dealFor(p, qrMode ? "dine_in" : channel)} onOpen={setDetailProduct} categories={menuCats} />
    </div>
  );
  // Variation chip card is deferred until the POS feed ships real groups; a group entry renders its real member dishes as flat cards.
  const renderEntry = (e) => (e.kind === "group" ? e.group.options.map((o) => renderDish(o.product)) : renderDish(e.product));

  return (
    <main className="ut-page min-h-screen" data-testid="business-page">
      {festive ? (
        <section className="border-b" style={{ background: "var(--ut-panel)", borderColor: "var(--ut-line)" }} data-testid="store-hero-banner">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-12 sm:pt-20 sm:pb-16 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div>
              <p className="ut-eyebrow">{business.slug === "express" ? "Indian quick bites · Punjabi flavour · Lincoln Park" : "Indian Punjabi kitchen · Lincoln Park"}</p>
              <h1 className="ut-h1 mt-4 text-4xl sm:text-5xl lg:text-6xl">{business.seo?.h1 || business.name}</h1>
              {business.slug === "express" && business.tagline && <p className="ut-lead mt-5">{business.tagline}</p>}
            </div>
            <p className="text-sm sm:text-base font-medium sm:text-right" style={{ color: "var(--ut-ink)" }} data-testid="store-hours-line">
              Open 2–11 PM · closed Tuesdays<br />
              Dine-in, pickup{deliveryInfo?.available && deliveryInfo.customer_fee != null ? ` & delivery · $${Number(deliveryInfo.customer_fee).toFixed(2)}${deliveryInfo.free_delivery_over > 0 ? `, free over $${Number(deliveryInfo.free_delivery_over).toFixed(0)}` : ""}` : " & delivery"}
            </p>
          </div>
        </section>
      ) : (
      <button type="button" onClick={() => { if (!editMode) heroClick(); }} aria-label={`See the featured dish on the ${business.name} menu`}
        className="relative block w-full h-72 sm:h-[24rem] overflow-hidden text-left cursor-pointer group" data-testid="store-hero-banner">
        <EditEntityButton type="business" entity={business} label="Edit brand (name, tagline, hero image, SEO)" className="absolute top-4 right-4" onSaved={setBusiness} />
        {HERO_VIDEOS[business.slug] ? (
          <video poster={business.image} autoPlay muted loop playsInline preload="auto"
            ref={(el) => { if (el) { el.muted = true; el.play().catch(() => {}); } }}
            onCanPlay={(e) => { e.currentTarget.muted = true; e.currentTarget.play().catch(() => {}); }}
            className="w-full h-full object-cover" data-testid="store-hero-video">
            <source src={HERO_VIDEOS[business.slug]} type="video/mp4" />
            <source src={HERO_VIDEOS[business.slug].replace(".mp4", ".webm")} type="video/webm" />
          </video>
        ) : (
          <img {...imgProps(business.image, [800, 1200, 1600], "100vw")} alt={business.seo?.hero_alt || business.name} width="1600" height="384" loading="eager" fetchPriority="high" className="w-full h-full object-cover hero-kenburns" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/40" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.5) 100%)" }} />
        <div className="absolute bottom-0 max-w-7xl w-full left-1/2 -translate-x-1/2 px-4 sm:px-6 pb-8 sm:pb-10">
          <Editable as="p" k={`store.${business.slug}.kicker`} fallback={HERO_KICKERS[business.slug] || business.tagline} label="Hero kicker" className="hero-fade-up text-[11px] sm:text-xs font-bold tracking-[0.35em] uppercase text-[#F0B429]" testId="store-hero-kicker" />
          <h1 className="hero-fade-up [animation-delay:120ms] font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white mt-2 drop-shadow-[0_6px_30px_rgba(0,0,0,0.7)]">
            {business.seo?.h1 || business.name}
          </h1>
          {STORE_SEO_LINE[business.slug] && <p className="hero-fade-up [animation-delay:180ms] text-white/90 text-sm sm:text-base mt-3 max-w-2xl" data-testid="store-seo-line">{STORE_SEO_LINE[business.slug]}</p>}
          <p className="hero-fade-up [animation-delay:240ms] text-white/85 text-sm sm:text-base mt-2.5">
            {business.tagline} · <span className="underline underline-offset-4 decoration-[#F0B429]/60 group-hover:decoration-[#F0B429] transition-colors"><Editable k={`store.${business.slug}.browse`} fallback="Browse the menu ↓" label="Browse-the-menu text" /></span>
          </p>
        </div>
      </button>
      )}
      <div className={`flex justify-end ${festive ? "mt-2 mb-0" : "-mt-14 mb-8"} relative z-10 px-4 pointer-events-none`} data-testid="store-share-row">
        <span className="pointer-events-auto" onClick={(e) => e.stopPropagation()}>
          <ShareButton tone="light" testId="share-menu" title={`${business.name} — menu`} text={`${business.name} · ${business.tagline}`} path={`/store/${business.slug}`} />
        </span>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {business.slug === "restaurant" && (
          <p className="text-sm text-neutral-700 mb-3" data-testid="restaurant-halwai-link">
            <RouterLink to="/store/halwai" className="underline font-semibold">Indian Punjabi mithai boxes (1/2 lb) at Urban Halwai</RouterLink>
          </p>
        )}
        {business.slug === "halwai" && (
          <>
            <p className="text-base md:text-lg text-neutral-800 max-w-3xl mb-2 leading-relaxed" data-testid="halwai-intro">{HALWAI_INTRO}</p>
            <p className="text-base text-neutral-800 max-w-3xl mb-4 leading-relaxed" data-testid="halwai-pickup-line">{HALWAI_PICKUP_LINE}</p>
            {mithai.length > 0 && (
              <div className="mb-6 max-w-3xl" data-testid="halwai-box-links">
                <p className="text-sm font-bold text-neutral-700 mb-2">Every box has its own page:</p>
                <div className="flex flex-wrap gap-2">
                  {mithai.map((m) => (
                    <RouterLink key={m.slug} to={`/store/halwai/${m.slug}`} data-testid={`halwai-box-link-${m.slug}`}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-neutral-200 hover:border-black inline-block">
                      {m.sweet} Box {m.price_label}
                    </RouterLink>
                  ))}
                  <RouterLink to="/diwali-mithai-chicago" data-testid="halwai-diwali-link"
                    className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#E04F33] text-white hover:bg-[#C94228] inline-block">
                    Diwali Mithai Boxes
                  </RouterLink>
                </div>
              </div>
            )}
          </>
        )}
        {festive ? <ThisWeekStrip cards={weekCards} onSelect={selectFeatured} showBogo={business.slug === "restaurant" && !qrMode && products.some((p) => p.bogo)} /> : <OffersStrip businessId={business.id} onSelect={selectFeatured} />}
        {qrMode && (
          <div className="mb-5 flex items-center gap-3 bg-[#0F0F0F] text-white rounded-2xl px-5 py-4" data-testid="table-banner">
            <QrCode size={20} aria-hidden="true" />
            <div>
              <p className="font-bold text-sm">Table {tableNumber} · Dine-in</p>
              <p className="text-xs text-white/60">Your order will be sent to this table. Pay in app or at the counter.</p>
            </div>
          </div>
        )}

        {groceryDeals.length > 0 && (
          <section className="mb-8" data-testid="grocery-deals-strip">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="font-display text-lg font-extrabold tracking-tight">This week's grocery deals</h2>
              <span className="text-xs font-semibold text-[#E04F33]">New deals every Monday</span>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
              {groceryDeals.map((p) => (
                <div key={p.id} className="w-56 shrink-0">
                  <ProductCard product={{ ...p, business_name: business.name }} deal={dealFor(p, qrMode ? "dine_in" : channel)} onOpen={setDetailProduct} />
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-6 min-h-[2.5rem]" data-testid="channel-selector">
          {availableChannels.map((ch) => {
            const meta = CHANNEL_META[ch];
            if (!meta) return null;
            const Icon = meta.icon;
            const active = channel === ch;
            if (ch === "delivery" && !deliveryInfo?.available) return <span key={ch} className="inline-flex" data-testid="channel-delivery"><DeliveryBadges compact testPrefix="toolbar-delivery" /></span>;
            return (
              <button key={ch} data-testid={`channel-${ch}`} disabled={qrMode}
                onClick={() => setChannel(ch)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${active ? "bg-[#E04F33] text-white" : "bg-white border border-neutral-200 text-neutral-600 hover:border-[#E04F33]"}`}>
                <Icon size={15} aria-hidden="true" /> {meta.label}
              </button>
            );
          })}
          <span className="text-xs text-neutral-400 ml-1">Menu & prices update per channel</span>
          {!festive && business.slug === "restaurant" && deliveryInfo?.available && deliveryInfo.customer_fee != null && deliveryInfo.free_delivery_over > 0 && (
            <span className="text-xs text-neutral-400" data-testid="delivery-terms-line">Delivery ${Number(deliveryInfo.customer_fee).toFixed(2)} · free on orders ${Number(deliveryInfo.free_delivery_over).toFixed(0)}+</span>
          )}
        </div>

        {festive && !activeGroup && <MenuCategoryBoxes groups={groups} search={search} onSearch={setSearch} buildLink={business.slug === "restaurant"} />}
        {searching && <MenuSearchResults query={search} entries={searchHits} onClear={() => setSearch("")} renderCard={renderEntry} />}
        {festive && !activeGroup && !searching && <OurStoryTeaser story={seoContent.story} image={business.image} />}
        {!festive && <div ref={gridRef} className="flex gap-2 overflow-x-auto pb-2 mb-6 scroll-mt-24" data-testid="category-filter">
          <button onClick={() => setActiveCat("all")} data-testid="category-all"
            className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap ${activeCat === "all" ? "bg-[#0F0F0F] text-white" : "bg-white border border-neutral-200 text-neutral-600"}`}>
            All
          </button>
          {shownCats.map((c) => (
            <span key={c.id} className="relative inline-flex shrink-0">
            <button onClick={() => setActiveCat(c.id)} data-testid={`category-${c.id}`}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap ${c.hidden || (editMode && !hasVisible(c)) ? "opacity-40 line-through" : ""} ${activeCat === c.id ? "bg-[#0F0F0F] text-white" : "bg-white border border-neutral-200 text-neutral-600"}`}>
              {c.name}
            </button>
            {!c.virtual && <EditEntityButton type="category" entity={c} className="absolute -top-3 -right-2" onSaved={reloadCategories} />}
            </span>
          ))}
        </div>}

        {business.slug === "restaurant" && !qrMode && products.some((p) => p.bogo) && (
          <section id="bogo" data-testid="bogo-deals-section" className="mb-10 rounded-2xl p-5 sm:p-6"
            style={{ background: "linear-gradient(135deg, #171007, #2E2208 60%, #171007)" }}
            ref={(el) => { if (el && window.location.hash === "#bogo" && !window.__bogoScrolled) { window.__bogoScrolled = true; setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 350); } }}>
            <h2 className="text-lg md:text-xl font-extrabold" style={{ color: "#F3D27A" }}>BOGO Deals: Buy One, Get One Free</h2>
            <p className="text-sm mb-4" style={{ color: "#C9A95C" }}>Indian Punjabi favorites. Online pickup and delivery only. Add 2 of the same dish and the 2nd one is free at checkout.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.filter((p) => p.bogo && p.in_stock !== false).map((p) => (
                <ProductCard key={`bogo-${p.id}`} product={{ ...p, business_name: business.name }} onOpen={setDetailProduct} />
              ))}
            </div>
          </section>
        )}
        {festive ? (
          activeGroup && <MenuCategoryView group={activeGroup} onBack={backToMenu} renderCard={renderEntry} />
        ) : visible.length === 0 ? (
          <p className="text-center text-neutral-400 py-16" data-testid="no-products">
            No items available for {CHANNEL_META[qrMode ? "dine_in" : channel]?.label.toLowerCase()} right now.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pb-16" data-testid="product-grid">
            {visible.map((p) => (
              <div key={p.id} id={`prod-${p.id}`}
                className={highlightId === p.id ? "ring-2 ring-[#E04F33] ring-offset-2 rounded-2xl transition-shadow" : ""}>
                <ProductCard product={{ ...p, business_name: business.name }} deal={dealFor(p, qrMode ? "dine_in" : channel)} onOpen={editMode ? undefined : setDetailProduct}
                  onEdited={onProductEdited} categories={categories} />
              </div>
            ))}
          </div>
        )}
      </div>
      {business.slug === "halwai" && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-12" data-testid="halwai-faq">
          <h2 className="text-base md:text-lg font-extrabold">Mithai box questions</h2>
          <div className="mt-2 divide-y divide-neutral-200 max-w-3xl">
            {HALWAI_FAQ.map(([q, a], i) => (
              <div key={q} className="py-4" data-testid={`halwai-faq-${i + 1}`}>
                <p className="font-extrabold text-base">{q}</p>
                <p className="text-base text-neutral-800 mt-1 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </section>
      )}
      {business.slug !== "halwai" && <DeliveryBand />}
      <ItemDetailSheet product={detailProduct} businessName={business.name}
        deal={detailProduct ? dealFor(detailProduct, qrMode ? "dine_in" : channel) : null}
        open={!!detailProduct} onOpenChange={(o) => { if (!o) setDetailProduct(null); }} />
    </main>
  );
}
